import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import PageTransition from "../components/PageTransition";
import LoadingSpinner from "../components/LoadingSpinner";
import InfiniteCarousel from "../components/InfiniteCarousel";
import { galleryApi } from "../api/galleryApi";

const API_BASE = import.meta.env.VITE_BACKEND_URL || "http://127.0.0.1:8000";

export default function Gallery() {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageTitle, setImageTitle] = useState('');

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await galleryApi.getImages();
      if (response.success) {
        setImages(response.images || []);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('title', imageTitle || 'Apartemen Image');
    formData.append('is_active', '1');

    try {
      const response = await galleryApi.uploadImage(formData);

      if (response.success) {
        setImages([...images, response.image]);
        setSelectedFile(null);
        setPreviewUrl(null);
        setImageTitle('');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await galleryApi.deleteImage(imageId);
      setImages(images.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const toggleImageStatus = async (imageId, currentStatus) => {
    try {
      await galleryApi.updateImageStatus(imageId, !currentStatus);
      setImages(images.map(img =>
        img.id === imageId ? { ...img, is_active: !currentStatus } : img
      ));
    } catch (error) {
      console.error('Error updating image status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="p-6 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg p-6`}
        >
          <h1 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <i className="fas fa-images mr-3"></i>
            Galeri Apartemen
          </h1>

          {/* Upload Section */}
          <div className={`border-2 border-dashed rounded-xl p-6 mb-6 ${
            isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'
          }`}>
            <div className="text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`cursor-pointer inline-flex items-center px-4 py-2 rounded-lg ${
                  isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                } text-white transition-colors`}
              >
                <i className="fas fa-upload mr-2"></i>
                Pilih Gambar
              </label>

              {previewUrl && (
                <div className="mt-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-xs mx-auto rounded-lg shadow-md"
                  />
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="Judul gambar (opsional)"
                      value={imageTitle}
                      onChange={(e) => setImageTitle(e.target.value)}
                      className={`w-full max-w-xs px-3 py-2 rounded-lg border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      } mb-3`}
                    />
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className={`px-4 py-2 rounded-lg ${
                        isDark ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors disabled:opacity-50`}
                    >
                      {uploading ? 'Mengupload...' : 'Upload Gambar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Carousel Preview */}
          {images.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Preview Galeri (Auto Slide)
              </h2>
              <InfiniteCarousel images={images.filter(img => img.is_active)} />
            </div>
          )}

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  delay: (index % 8) * 0.1,
                  duration: 0.5,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`relative group rounded-xl overflow-hidden shadow-lg ${
                  isDark ? 'bg-gray-700' : 'bg-white'
                }`}
              >
                <img
                  src={
                    image.image_url
                      ? image.image_url
                      : `${API_BASE}/storage/${image.image_path || image.image}`
                  }
                  alt={image.title || 'Gallery Image'}
                  className="w-full h-48 object-cover"
                />

                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                  <button
                    onClick={() => toggleImageStatus(image.id, image.is_active)}
                    className={`p-2 rounded-full transition-colors ${
                      image.is_active ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
                    } text-white`}
                  >
                    <i className={`fas ${image.is_active ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>

                  <button
                    onClick={() => handleDelete(image.id)}
                    className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>

                <div className="p-3">
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {image.title || 'Gambar Apartemen'}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

        </motion.div>
      </div>
    </PageTransition>
  );
}